/* In-app (bell) alerts — client state: the unread badge, the pop-up queue, the bell list and
 * the open alert. The poll runs every POLL_MS after sign-in (AppShell → startInboxPolling).
 *
 * Pop-up rules:
 *  • the FIRST poll after sign-in asks with no cursor → the server returns only alerts still
 *    waiting for Acknowledge, so a sign-in is never flooded with old alerts;
 *  • every later poll passes the server's `nextSince` cursor → the alerts created since then;
 *  • the cursor overlaps by a few seconds, so ids already shown or dismissed are dropped here. */
import { create } from 'zustand';
import * as inboxApi from './inboxApi';
import type { InAppAlert } from './inboxApi';

export const POLL_MS = 30_000;
const BELL_PAGE_SIZE = 10;

interface InboxState {
  unread: number;
  awaitingAck: number;
  /** Alerts waiting to be shown (or still showing) as pop-ups, oldest first. */
  popups: InAppAlert[];
  /** Ids already shown in this browser session — never popped twice. */
  seen: Set<string>;
  nextSince: string | null;
  /** The bell drop-down list. */
  list: InAppAlert[];
  listTotal: number;
  listPage: number;
  listLoading: boolean;
  bellOpen: boolean;
  /** The alert open in the detail pop-up (null = closed). */
  detailId: string | null;

  poll: () => Promise<void>;
  dismissPopup: (id: string) => void;
  setBellOpen: (open: boolean) => void;
  loadList: (page?: number) => Promise<void>;
  openDetail: (id: string) => void;
  closeDetail: () => void;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<number>;
  acknowledge: (id: string) => Promise<void>;
  /** Test / sign-out reset. */
  reset: () => void;
}

const initial = {
  unread: 0, awaitingAck: 0, popups: [] as InAppAlert[], seen: new Set<string>(), nextSince: null as string | null,
  list: [] as InAppAlert[], listTotal: 0, listPage: 0, listLoading: false, bellOpen: false, detailId: null as string | null,
};

/** Stamp an alert read/acknowledged wherever it is held, so every view agrees at once. */
function patchEverywhere(s: InboxState, id: string, patch: Partial<InAppAlert>): Partial<InboxState> {
  const apply = (a: InAppAlert) => (a.id === id ? { ...a, ...patch } : a);
  return { list: s.list.map(apply), popups: s.popups.map(apply) };
}

export const useInboxStore = create<InboxState>((set, get) => ({
  ...initial,
  seen: new Set<string>(),

  poll: async () => {
    const summary = await inboxApi.getSummary(get().nextSince);
    set((s) => {
      const seen = new Set(s.seen);
      // Oldest first, so the newest lands at the end of the stack.
      const fresh = [...summary.popups].reverse().filter((a) => !seen.has(a.id));
      for (const a of fresh) seen.add(a.id);
      return {
        unread: summary.unread,
        awaitingAck: summary.awaitingAck,
        nextSince: summary.nextSince,
        seen,
        popups: fresh.length ? [...s.popups, ...fresh] : s.popups,
      };
    });
    // Keep an open bell list current with what just arrived.
    if (get().bellOpen && summary.popups.length) void get().loadList(1);
  },

  dismissPopup: (id) => set((s) => ({ popups: s.popups.filter((a) => a.id !== id) })),

  setBellOpen: (open) => {
    set({ bellOpen: open });
    if (open) void get().loadList(1);
  },

  loadList: async (page = 1) => {
    set({ listLoading: true });
    try {
      const res = await inboxApi.getAlerts(page, BELL_PAGE_SIZE);
      set((s) => ({
        list: page === 1 ? res.items : [...s.list, ...res.items.filter((a) => !s.list.some((x) => x.id === a.id))],
        listTotal: res.total,
        listPage: page,
        unread: res.unread,
      }));
    } finally {
      set({ listLoading: false });
    }
  },

  openDetail: (id) => {
    set({ detailId: id, bellOpen: false });
    // Reading is a convenience flag: if it fails the alert simply stays unread for the next poll.
    get().markRead(id).catch((err: unknown) => {
      // eslint-disable-next-line no-console
      console.warn('[inbox] mark-read failed', err);
    });
  },

  closeDetail: () => set({ detailId: null }),

  markRead: async (id) => {
    const wasUnread = [...get().list, ...get().popups].some((a) => a.id === id && !a.readAt);
    await inboxApi.markRead(id);
    const now = new Date().toISOString();
    set((s) => ({ ...patchEverywhere(s, id, { readAt: now }), unread: wasUnread ? Math.max(0, s.unread - 1) : s.unread }));
  },

  markAllRead: async () => {
    const changed = await inboxApi.markAllRead();
    const now = new Date().toISOString();
    set((s) => ({ unread: 0, list: s.list.map((a) => (a.readAt ? a : { ...a, readAt: now })) }));
    return changed;
  },

  acknowledge: async (id) => {
    await inboxApi.acknowledge(id);
    const now = new Date().toISOString();
    set((s) => ({
      ...patchEverywhere(s, id, { ackAt: now, readAt: now }),
      popups: s.popups.filter((a) => a.id !== id),
      awaitingAck: Math.max(0, s.awaitingAck - 1),
    }));
  },

  reset: () => set({ ...initial, seen: new Set<string>() }),
}));

/** Start the ~30 s poll (first poll immediately). Returns the stop function. A failed poll is
 *  retried on the next tick — the bell is a convenience, never a reason to interrupt work. */
export function startInboxPolling(intervalMs: number = POLL_MS): () => void {
  let stopped = false;
  const tick = () => {
    if (stopped) return;
    useInboxStore.getState().poll().catch((err: unknown) => {
      // eslint-disable-next-line no-console
      console.warn('[inbox] poll failed — retrying on the next tick', err);
    });
  };
  tick();
  const timer = setInterval(tick, intervalMs);
  return () => {
    stopped = true;
    clearInterval(timer);
    useInboxStore.getState().reset();
  };
}
